import { useParams, Link } from "react-router-dom";
import { ArrowRight, Check, MapPin } from "lucide-react";
import { SPACES_BY_SLUG, BRAND, FAQS } from "@/lib/site";
import InquiryForm from "@/components/site/InquiryForm";
import ContactPanel from "@/components/site/ContactPanel";
import FAQSection from "@/components/site/FAQSection";
import FinalCTA from "@/components/site/FinalCTA";
import SEO, { breadcrumbsLd, faqLd, placeOfferLd } from "@/components/site/SEO";
import { TESTIDS } from "@/constants/testIds/site";

const NotFound = () => (
  <section className="bg-[var(--bg)] min-h-[60vh] grid place-items-center">
    <div className="text-center px-6">
      <div className="eyebrow">404</div>
      <h1 className="font-serif text-[48px] mt-3">Space not found</h1>
      <p className="text-[var(--text-muted)] mt-3">That workspace doesn&apos;t exist — yet.</p>
      <Link to="/available-spaces" className="link-underline mt-6 inline-flex">
        Browse all spaces <ArrowRight size={14} />
      </Link>
    </div>
  </section>
);

const SpaceDetail = () => {
  const { slug } = useParams();
  const s = SPACES_BY_SLUG[slug];
  if (!s) return <NotFound />;

  return (
    <>
      <SEO
        title={`${s.name} for Lease in Weston, FL — ${s.suiteSize}`}
        description={`${s.short} From ${s.startingRate} ${s.rateUnit}. ${s.availability} now at Weston Commercial Center.`}
        path={`/spaces/${s.slug}`}
        image={s.image}
        jsonLd={[
          breadcrumbsLd([
            { name: "Home", path: "/" },
            { name: "Available Spaces", path: "/available-spaces" },
            { name: s.name, path: `/spaces/${s.slug}` },
          ]),
          placeOfferLd(s),
          faqLd(FAQS),
        ]}
      />
      {/* HERO */}
      <section data-testid={TESTIDS.spaceDetailHero} className="bg-[var(--bg)] border-b border-[var(--border)]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-20 md:py-28">
          <Link to="/available-spaces" className="text-[12px] tracking-[0.18em] uppercase text-[var(--text-muted)] hover:text-[var(--primary)]">
            ← All spaces
          </Link>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
              <div className="eyebrow">{s.eyebrow}</div>
              <h1 className="hero-display text-[52px] md:text-[92px] mt-5 leading-[1.0]">
                {s.name}
              </h1>
              <p className="mt-7 max-w-xl text-[16px] leading-relaxed text-[var(--text-muted)]">
                {s.overview}
              </p>
              <div className="mt-9 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4">
                <div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--text-muted)]">Suite size</div>
                  <div className="mt-1 font-medium text-[14px]">{s.suiteSize}</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--text-muted)]">Capacity</div>
                  <div className="mt-1 font-medium text-[14px]">{s.capacity}</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--text-muted)]">Availability</div>
                  <div className="mt-1 font-medium text-[14px]">{s.availability}</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--text-muted)]">From</div>
                  <div className="mt-1 font-medium text-[14px] text-[var(--primary)]">
                    {s.startingRate} <span className="text-[var(--text-muted)] font-normal">{s.rateUnit}</span>
                  </div>
                </div>
              </div>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Link to={`/contact?type=${encodeURIComponent(s.name)}`} className="btn-primary justify-center">
                  Schedule a tour <ArrowRight size={14} />
                </Link>
                <a href={`tel:${BRAND.phoneRaw}`} className="btn-ghost justify-center">
                  Call {BRAND.phone}
                </a>
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="hairline aspect-[4/5] overflow-hidden">
                <img src={s.image} alt={s.name} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR + AMENITIES INCLUDED */}
      <section className="bg-white border-b border-[var(--border)]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="eyebrow">Who it&apos;s best for</div>
              <h2 className="section-title text-[36px] md:text-[48px] mt-4 leading-tight">
                Designed for the way you actually work.
              </h2>
              <ul className="mt-8 space-y-3">
                {s.bestFor.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[15px]">
                    <span className="w-7 h-px bg-[var(--secondary)] mt-3 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:col-span-7">
              <div className="eyebrow">What&apos;s included</div>
              <h2 className="section-title text-[36px] md:text-[48px] mt-4 leading-tight">
                Every lease comes with this.
              </h2>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                {s.features.map((f) => (
                  <div key={f} className="flex items-start gap-3 text-[14.5px]">
                    <Check size={16} strokeWidth={1.8} className="mt-1 text-[var(--primary)]" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="bg-[var(--bg)] border-b border-[var(--border)]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-20 md:py-28">
          <div className="eyebrow">Gallery</div>
          <h2 className="section-title text-[36px] md:text-[52px] mt-4 leading-tight">A look inside.</h2>
          <div className="mt-10 grid grid-cols-12 gap-3 md:gap-4">
            <figure className="col-span-12 md:col-span-7 aspect-[16/10] hairline overflow-hidden">
              <img src={s.gallery[0]} alt={`${s.name} 1`} className="w-full h-full object-cover" />
            </figure>
            <figure className="col-span-6 md:col-span-5 aspect-[4/5] hairline overflow-hidden">
              <img src={s.gallery[1] || s.image} alt={`${s.name} 2`} className="w-full h-full object-cover" />
            </figure>
            <figure className="col-span-6 md:col-span-5 aspect-[4/5] hairline overflow-hidden">
              <img src={s.gallery[2] || s.image} alt={`${s.name} 3`} className="w-full h-full object-cover" />
            </figure>
            <figure className="col-span-12 md:col-span-7 aspect-[16/10] hairline overflow-hidden">
              <img src={s.image} alt={`${s.name} cover`} className="w-full h-full object-cover" />
            </figure>
          </div>
        </div>
      </section>

      {/* FLOORPLANS */}
      <section className="bg-white border-b border-[var(--border)]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="eyebrow">Floor plans</div>
              <h2 className="section-title text-[36px] md:text-[52px] mt-4 leading-tight">
                A few of the <em className="italic text-[var(--primary)]">currently available</em> suites.
              </h2>
              <p className="mt-5 text-[15px] text-[var(--text-muted)] max-w-md leading-relaxed">
                Inventory changes weekly. Request availability and we&apos;ll send
                the most up-to-date list with dimensioned floor plans.
              </p>
            </div>
            <div className="md:col-span-7">
              <div className="hairline divide-y divide-[var(--border)]">
                {s.floorplans.map((f) => (
                  <div key={f.label} className="flex items-center justify-between p-6 bg-white">
                    <div>
                      <div className="font-serif text-[24px]">{f.label}</div>
                      <div className="text-[12px] mt-1 uppercase tracking-[0.16em] text-[var(--text-muted)]">{f.note}</div>
                    </div>
                    <div className="text-[14px] font-medium">{f.size}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-center gap-2 text-[12px] text-[var(--text-muted)]">
                <MapPin size={13} strokeWidth={1.5} />
                Floor plans and availability are illustrative — confirm at tour.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INQUIRY (two-column) */}
      <section className="bg-[var(--bg)] border-b border-[var(--border)]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-7">
              <InquiryForm
                defaultOfficeType={s.name}
                sourcePage={`space:${s.slug}`}
                heading={`Inquire about ${s.name}`}
                subheading="Send your details and we'll respond within one business hour with available suites that match."
              />
            </div>
            <div className="lg:col-span-5">
              <ContactPanel />
            </div>
          </div>
        </div>
      </section>

      <FAQSection />
      <FinalCTA />
    </>
  );
};

export default SpaceDetail;
